import { useCheckAccountTraits } from '@cmg/auth';
import { countriesList } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';

import { useGetOfferingCountryCode } from '../../../../../../../../common/hooks/useGetOfferingCountryCode';
import { isValidCmgEntityKey } from '../../../../../../../../common/util/isValidCmgEntityKey';
import { isInvestorUnlinked } from '../../../../../../../../common/util/rolodex/unlinked-investor.util';
import {
  AttestationFormStatus,
  CertificateType,
  OfferingType,
} from '../../../../../../../../graphql';
import offeringSidePanelSellSideRouteFactory from '../../../../../../../offering-side-panel/offeringSidePanelSellSideRouteFactory';
import { OrderBookPersonaType } from '../../../../../hooks/useIdentifyOrderBookPersona';
import { useSyndicateAttestationQuery } from '../../syndicate/useAttestationQuery';
import { CertificateTypeLabels } from '../Certificates.model';
import { CertificateModel, PersonaProps } from '../useCertificatesModel';

export type Props = PersonaProps & {
  offeringId: string;
  cmgEntityKey: string | undefined;
  certificateType: CertificateType | null;
  offeringType: OfferingType | undefined;
};

export const COMPLIANCE_WARNING_MESSAGE_SYNDICATE =
  'Please review the outstanding requirements in the Compliance section. To proceed with an indication, click ‘Continue to Submit Indication’';

export const QIB_COMPLIANCE_WARNING_MESSAGE =
  'Proceed only if you can ensure the Investor is a QIB. To proceed with an indication, click ‘Continue to Submit Indication’';

export const COMPLIANCE_TOOLTIP_WARNING_MESSAGE_SYNDICATE = `is required to participate in this offering. Click Copy link and provide the link to the investor to complete.`;

function getComplianceMessages(certificateType: CertificateType) {
  return {
    message:
      certificateType === CertificateType.Qib
        ? QIB_COMPLIANCE_WARNING_MESSAGE
        : COMPLIANCE_WARNING_MESSAGE_SYNDICATE,
    tooltipMessage: `${
      certificateType ? CertificateTypeLabels[certificateType] : CertificateTypeLabels.FORM51305131
    } ${COMPLIANCE_TOOLTIP_WARNING_MESSAGE_SYNDICATE}`,
  };
}

export const useSyndicateCertificatesModel = ({
  type,
  offeringId,
  cmgEntityKey,
  certificateType,
  offeringType,
}: Props): CertificateModel => {
  const isSyndicate = type === OrderBookPersonaType.SYNDICATE;
  const { message, tooltipMessage } = getComplianceMessages(certificateType!);
  const hasCertificateLibraryTrait = useCheckAccountTraits(['CERTIFICATE_LIBRARY']);
  const offeringCountryCode = useGetOfferingCountryCode(offeringId);
  const isUsOffering = offeringCountryCode === countriesList.Usa.countryCode;
  // Attestation
  const attestationQuery = useSyndicateAttestationQuery(
    {
      offeringId,
      cmgEntityKey: cmgEntityKey!,
      certificateType: certificateType!,
    },
    {
      skip:
        !isValidCmgEntityKey(cmgEntityKey) ||
        !isSyndicate ||
        !offeringId ||
        !certificateType ||
        !hasCertificateLibraryTrait,
    }
  );
  const { attestation = null } = attestationQuery.data ?? {};
  let indicationAttestation = defaultTo(attestationQuery.data?.indicationAttestation, null);

  if (!isUsOffering && indicationAttestation?.certificateType === CertificateType.Form51305131) {
    indicationAttestation = null;
  }

  const hasComplianceWarning =
    (indicationAttestation?.status === AttestationFormStatus.Expired ||
      indicationAttestation?.status === AttestationFormStatus.NotOnFile ||
      (isInvestorUnlinked(cmgEntityKey) ?? false)) &&
    (offeringType === OfferingType.Ipo || certificateType === CertificateType.Qib);

  const redirectUrl = cmgEntityKey
    ? offeringSidePanelSellSideRouteFactory.orderBookCompliance.getUrlPath({
        cmgEntityKey: cmgEntityKey,
      })
    : null;

  return {
    canRead: !!cmgEntityKey && isSyndicate,
    attestation: indicationAttestation,
    attestationForm: attestation?.attestationForm ?? null,
    loading: attestationQuery.loading,
    offeringType: offeringType,
    certificateType,
    warning: {
      hasComplianceWarning,
      canByPassComplianceWarning: true,
      message,
      redirectUrl,
      tooltipMessage,
    },
  };
};
